<template>
  <div class="step-bank-selection step-stagger">
    <h1 class="step-heading" dir="auto">{{ headingText }}</h1>

    <div class="banks-list">
      <button
        v-for="bank in banks"
        :key="bank.id"
        class="bank-card"
        @click="$emit('select', bank)"
      >
        <img class="bank-arrow" :src="arrowLeftIcon" alt="" />
        <span class="bank-name">{{ bank.name }}</span>
        <img
          v-if="bank.logo"
          class="bank-logo"
          :class="{ 'bank-logo--loaded': loadedLogos[bank.id] }"
          :src="bank.logo"
          :alt="bank.name"
          decoding="async"
          @load="loadedLogos = { ...loadedLogos, [bank.id]: true }"
        />
        <span v-else class="bank-logo-placeholder" />
      </button>
    </div>
  </div>
</template>

<script>
import { sortedBankSources } from '@/constants/sourcesConfiguration';
import OpenBankingMixin from '@/base-components/onboarding/openBanking/OpenBankingMixin.vue';
import BankAccounts from '@/mixins/BankAccounts.vue';

import { bankLogos } from '@/pages/responsive-pages/non-authenticated/mortgage-refinance/bankLogos';

const arrowLeftIcon = require('@/pages/responsive-pages/non-authenticated/mortgage-refinance/assets/lucide-arrow-left.svg');

// Per-flow display-name overrides. The global sourcesConfiguration is shared
// across the whole product, so we override here without touching it.
const BANK_NAME_OVERRIDES = {
  mizrahi: 'מזרחי טפחות',
};

// Banks we don't support in the mortgage-refinance flow.
const HIDDEN_BANK_IDS = new Set(['jerusalem', 'doar']);

export default {
  name: 'StepBankSelection',
  mixins: [OpenBankingMixin, BankAccounts],
  props: {
    // 'regular' (default) → "איפה חשבון הבנק שלך נמצא?"
    // 'mortgage'         → "איפה חשבון המשכנתא שלך נמצא?"
    mode: {
      type: String,
      default: 'regular',
      validator(value) {
        return ['regular', 'mortgage'].includes(value);
      },
    },
  },
  data() {
    return {
      arrowLeftIcon,
      // Per-bank load flags so each logo fades in over its placeholder once
      // the image actually arrives — avoids a hard flash on slow connections.
      loadedLogos: {},
    };
  },
  computed: {
    banks() {
      // Mortgage-refinance bank-select always shows One Zero, regardless of
      // the OPEN_ONE_ZERO feature flag. We bypass sortedCombinedBankSources
      // (which rejects onezero when the flag is off) and apply the rest of
      // the open-to-customer gate manually.
      return sortedBankSources()
        .filter(source => !HIDDEN_BANK_IDS.has(source.id))
        .filter(source => source.id === 'onezero' || this.isSourceOpenToCustomer(source.id))
        .map(source => ({
          id: source.id,
          name: BANK_NAME_OVERRIDES[source.id] || source.displayName,
          sourceName: source.sourceName,
          openBanking: source.openBanking,
          credentials: source.credentials,
          logoFile: source.logoFile,
          logo: bankLogos[source.id] || null,
        }));
    },
    headingText() {
      return this.mode === 'mortgage'
        ? 'איפה חשבון המשכנתא שלך נמצא?'
        : 'איפה חשבון הבנק שלך נמצא?';
    },
  },
  mounted() {
    // Cached images can finish loading before the @load listener is attached,
    // which would leave them stuck at opacity:0. Mark any already-complete
    // logos as loaded on mount to cover that race.
    this.$nextTick(() => {
      this.$el.querySelectorAll('.bank-logo').forEach(img => {
        if (img.complete && img.naturalWidth > 0) {
          const bankId = this.banks.find(b => b.logo === img.getAttribute('src'))?.id;
          if (bankId) { this.loadedLogos = { ...this.loadedLogos, [bankId]: true }; }
        }
      });
    });
  },
};
</script>

<style lang="scss" scoped>
.step-bank-selection {
  display: flex;
  flex-direction: column;
  padding: 24px 27px 0;
}

.step-heading {
  font-family: 'SimplerProAlte', sans-serif;
  font-size: 32px;
  line-height: 36px;
  font-weight: 900;
  font-style: normal;
  color: #205441;
  text-align: center;
  margin: 0 auto 34px;
  max-width: 326px;

  @media (max-width: 500px) {
    font-size: 26px;
    line-height: 32px;
  }
}

.banks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 24px;
}

.bank-card {
  display: flex;
  align-items: center;
  height: 96px;
  background: rgba(255, 255, 255, 0.67);
  border: none;
  border-radius: 32px;
  cursor: pointer;
  padding: 0 12px 0 24px;
  transition: transform 0.15s ease;
  // RTL: logo on right, name in middle, arrow on left
  flex-direction: row-reverse;
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: 0px 4px 4.7px rgba(0, 0, 0, 0.02),
                0px 20px 29.4px rgba(182, 173, 130, 0.27);
    z-index: -1;
  }

  &:active {
    transform: scale(0.97);
  }
}

.bank-logo {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  // Neutral placeholder fills the reserved 72x72 box until the image loads,
  // then the logo fades in — so a slow fetch resolves in place rather than
  // popping in or shifting layout.
  background: rgba(32, 84, 65, 0.06);
  opacity: 0;
  transition: opacity 0.25s ease;

  &--loaded {
    opacity: 1;
  }
}

.bank-logo-placeholder {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.bank-name {
  font-family: 'SimplerPro', sans-serif;
  font-size: 20px;
  line-height: 26px;
  font-weight: 700;
  color: #202125;
  flex: 1;
  text-align: right;
  padding: 0 16px;
}

.bank-arrow {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}
</style>
